import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/shared/contexts/ToastContext';
import { submitForm } from '@/shared/api';
import type { FormType } from '@/shared/api';

const emailFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
});

export type EmailFormData = z.infer<typeof emailFormSchema>;

export interface EmailCaptureFormProps {
  onSubmit?: (data: EmailFormData) => Promise<void> | void;
  submitText?: string;
  submittingText?: string;
  placeholder?: string;
  variant?: 'hero-24' | 'hero-14' | 'default';
  className?: string;
  onSuccessMessage?: string;
  formType?: FormType;
}

export const EmailCaptureForm = ({
  onSubmit,
  submitText = 'Get Started',
  submittingText = 'Submitting...',
  placeholder = 'Your email address',
  variant = 'default',
  className = '',
  onSuccessMessage,
  formType
}: EmailCaptureFormProps) => {
  const { notify } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema)
  });

  // Map variant to form type if not explicitly provided
  const getFormType = (): FormType => {
    if (formType) return formType;

    switch (variant) {
      case 'hero-24':
      case 'hero-14':
        return 'hero-email-capture';
      default:
        return 'hero-email-capture';
    }
  };

  const handleFormSubmit = async (data: EmailFormData) => {
    try {
      // Submit to API to save to file
      if (formType || variant !== 'default') {
        try {
          await submitForm({
            email: data.email,
            formType: getFormType()
          });
        } catch (apiError) {
          // Log error but don't block the user experience
          console.error('Failed to save form submission:', apiError);
        }
      }

      // Call custom onSubmit if provided
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Default behavior: simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      notify(
        'success',
        onSuccessMessage || "Thank you! We'll be in touch soon."
      );
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      notify('error', 'Something went wrong. Please try again.');
    }
  };

  // Variant-specific class names
  const getVariantClasses = () => {
    switch (variant) {
      case 'hero-24':
        return {
          container:
            'quick-form form-shadow mt--45 mt-[45px] lg:max-xl:!mt-[35px] md:max-lg:!mt-[35px] mx-[7%] sm:max-md:mx-[4%] xsm:max-sm:mx-[4%]',
          inputGroup:
            'input-group [border:none] relative flex flex-wrap items-stretch w-full',
          input:
            'form-control email r-06 rounded-[6px] xl:!h-[64px] bg-white !text-[#666] xl:!text-[1.125rem] !font-normal [transition:all_450ms_ease-in-out] p-[0_20px] [border:2px_solid_transparent] lg:max-xl:!h-[60px] lg:max-xl:!text-[1.15rem] md:max-lg:!text-[1.125rem] md:max-lg:!h-[52px] sm:max-md:!text-[1.175rem] sm:max-md:!h-[60px] xsm:max-sm:!text-[1.175rem] xsm:max-sm:!h-[58px] focus:bg-white focus:shadow-none focus:border-[#3eb1ff] focus:[outline:0] placeholder:text-[#999] relative flex-[1_1_auto] !w-[1%] min-w-0 block leading-[1.5] bg-clip-padding appearance-none shadow-[0_5px_10px_rgba(0,0,0,0.05)]',
          button:
            'btn r-06 !rounded-[6px] btn--theme hover--theme submit xl:!h-[64px] xl:!text-[1.0625rem] ml-[15px] !px-[45px] !py-[12px] lg:max-xl:!h-[60px] lg:max-xl:!text-[1.15rem] md:max-lg:!text-[1.125rem] md:max-lg:!h-[52px] sm:max-md:!text-[1.175rem] sm:max-md:!h-[60px] xsm:max-sm:!text-[1.175rem] xsm:max-sm:!h-[58px]',
          error: 'quick-form-msg',
          errorText:
            'loading !text-[1.125rem] !text-[#3eb1ff] leading-none font-medium'
        };
      case 'hero-14':
        return {
          container:
            'quick-form form-shadow mx-[6%] lg:max-xl:mx-[7%] md:max-lg:mx-[7%] sm:max-md:mx-[4%] xsm:max-sm:mx-[4%]',
          inputGroup:
            'input-group [border:none] relative flex flex-wrap items-stretch w-full',
          input:
            'form-control email r-06 rounded-[6px] xl:!h-[64px] bg-white !text-[#666] xl:!text-[1.125rem] !font-normal [transition:all_450ms_ease-in-out] p-[0_20px] [border:2px_solid_transparent] lg:max-xl:!h-[60px] lg:max-xl:!text-[1.15rem] md:max-lg:!text-[1.125rem] md:max-lg:!h-[52px] sm:max-md:!text-[1.175rem] sm:max-md:!h-[60px] xsm:max-sm:!text-[1.175rem] xsm:max-sm:!h-[58px] focus:bg-white focus:shadow-none focus:border-[#3eb1ff] focus:[outline:0] placeholder:text-[#999] relative flex-[1_1_auto] !w-[1%] min-w-0 block leading-[1.5] bg-clip-padding appearance-none',
          button:
            'btn r-06 !rounded-[6px] btn--theme hover--theme submit xl:!h-[64px] xl:!text-[1.0625rem] ml-[15px] !px-[45px] !py-[12px] lg:max-xl:!h-[60px] lg:max-xl:!text-[1.15rem] md:max-lg:!text-[1.125rem] md:max-lg:!h-[52px] sm:max-md:!text-[1.175rem] sm:max-md:!h-[60px] xsm:max-sm:!text-[1.175rem] xsm:max-sm:!h-[58px]',
          error:
            'quick-form-msg xl:!mb-[-15px] mt-[20px] mx-0 lg:max-xl:!mb-[-5px] lg:max-xl:!mt-[20px] lg:max-xl:mx-0 md:max-lg:!mb-[-5px] md:max-lg:!mt-[20px] md:max-lg:mx-0 sm:max-md:!mb-[-5px] sm:max-md:!mt-[20px] sm:max-md:mx-0 xsm:max-sm:!mt-[20px] xsm:max-sm:!mb-0 xsm:max-sm:mx-0 w-full block',
          errorText:
            'loading !text-[1.125rem] !text-[#3eb1ff] leading-none font-medium'
        };
      default:
        return {
          container: '',
          inputGroup: '',
          input: '',
          button: '',
          error: '',
          errorText: ''
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <form
      className={`${variantClasses.container} ${className}`}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <div className={variantClasses.inputGroup}>
        <input
          type="email"
          {...register('email')}
          className={variantClasses.input}
          placeholder={placeholder}
          autoComplete="off"
          required
        />
        <span className="input-group-btn form-btn">
          <button
            type="submit"
            disabled={isSubmitting}
            className={variantClasses.button}
          >
            {isSubmitting ? submittingText : submitText}
          </button>
        </span>
      </div>
      {errors.email && (
        <div className={variantClasses.error}>
          <span className={variantClasses.errorText}>
            {errors.email.message}
          </span>
        </div>
      )}
    </form>
  );
};
